import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { FacetItem, SelectedFiltersMapByProfiles } from '@modules/search/types'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { numberWithCommas, removeObjProperty } from '@shared/utils'

import {
  FILTER_LABELS_MAP,
  FILTER_SECTION_REQUIRES_SEARCH,
  GENERIC_FILTER_SELECT_LIMIT,
  PROFILE_FILTERS_ORDER,
} from '../../constants'
import GenericFilterSelect from './GenericFilterSelect'
import {
  BorderlessAccordion,
  CustomAccordionDetails,
  CustomAccordionSummary,
  FilterBadge,
  FilterSkeleton,
  FilterSkeletonBox,
  FilterTitleBox,
  OuterFilterBox,
} from './index.styles'

/** THIS FILE IS A BASE FOR FUTURE REFACTOTING */
interface FilterSectionProps {
  name: string
  handleFilterSelect: (type: string, field: string, value: string, direction: boolean, multiple?: boolean) => void
  handleClearSection: (type: string, field: string) => void
  handleExpand: (name: string, key: string, value: boolean, nestedName?: string) => void
  expandedFilterSection: Record<string, string | boolean>
  selectedValues: SelectedFiltersMapByProfiles[keyof SelectedFiltersMapByProfiles]
  facets: FacetItem[]
  label: string
  totalCount: number
  active: boolean
  filterSectionExpandAllOptions: boolean
  filterSectionOptionsSearch: string
  setFilterSectionExpandAllOptions: (value: boolean) => void
  setFilterSectionOptionsSearch: (value: string) => void
}

const FilterSection = memo(
  ({
    name,
    handleFilterSelect,
    handleClearSection,
    facets,
    selectedValues,
    totalCount,
    label,
    active,
    handleExpand,
    expandedFilterSection,
    filterSectionExpandAllOptions,
    filterSectionOptionsSearch,
    setFilterSectionExpandAllOptions,
    setFilterSectionOptionsSearch,
  }: FilterSectionProps) => {
    const [isExpanded, setIsExpanded] = useState(true)

    const toggleExpand = useCallback(() => {
      setIsExpanded(prev => !prev)
    }, [])
    const selectedValuesMapByFields = useMemo<Record<string, string[]>>(
      () =>
        selectedValues?.reduce(
          (acc, i) => ({ ...acc, [i.field]: [...(acc[i.field] || []), i.value] }),
          {} as Record<string, string[]>,
        ) || {},
      [selectedValues],
    )

    const onChangeFilter = useCallback(
      (key: string, value: string, direction: boolean, multiple?: boolean) =>
        handleFilterSelect(name, key, value, direction, multiple),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [name],
    )

    const onExpand = useCallback(
      (key: string, value: boolean, nestedName?: string) => handleExpand(name, key, value, nestedName),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [name],
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onClearSection = useCallback((field: string) => handleClearSection(name, field), [name])

    return (
      <OuterFilterBox $active={active} data-testid="search_filter_select">
        <BorderlessAccordion disableGutters expanded={isExpanded} onChange={toggleExpand}>
          <CustomAccordionSummary
            id={`search-filter-${name.toLowerCase()}_section`}
            expandIcon={
              <ExpandMoreIcon
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(90deg)',
                  transition: 'transform 0.2s ease-in-out',
                  transformOrigin: 'center',
                }}
              />
            }
          >
            <FilterTitleBox>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h2">{label}</Typography>
                {!!selectedValues?.length && (
                  <FilterBadge variant="dot" color="primary" data-testid={`${name.toLowerCase()}_filter_badge`} />
                )}
              </Box>
              {!!totalCount && <Typography variant="body1">({numberWithCommas(totalCount)})</Typography>}
            </FilterTitleBox>
          </CustomAccordionSummary>
          <CustomAccordionDetails>
            {facets.map(f =>
              f.key === FILTER_SECTION_REQUIRES_SEARCH ? (
                <GenericFilterSelect
                  key={f.key}
                  data={f}
                  selectedValues={selectedValuesMapByFields[f.key]}
                  onChange={onChangeFilter}
                  onClearSection={onClearSection}
                  onExpand={onExpand}
                  expandedFilter={expandedFilterSection}
                  filterName={name}
                  limit={GENERIC_FILTER_SELECT_LIMIT}
                  searchString={filterSectionOptionsSearch}
                  setSearchString={setFilterSectionOptionsSearch}
                  searchBoxId={`search-filer-${name}-country-keyword_box`}
                  // allOptionsExpanded={filterSectionExpandAllOptions}
                />
              ) : (
                <GenericFilterSelect
                  key={f.key}
                  data={f}
                  selectedValues={selectedValuesMapByFields[f.key]}
                  onChange={onChangeFilter}
                  onClearSection={onClearSection}
                  onExpand={onExpand}
                  expandedFilter={expandedFilterSection}
                  filterName={name}
                />
              ),
            )}
          </CustomAccordionDetails>
        </BorderlessAccordion>
      </OuterFilterBox>
    )
  },
)

const Filters = memo(
  ({
    filtersSetup: propsFiltersSetup,
    onFilterSelect,
    onClearSection,
    onExpand,
    selectedFiltersMap,
    expandedFilter,
    filterSectionExpandAllOptions,
    filterSectionOptionsSearch,
    setFilterSectionExpandAllOptions,
    setFilterSectionOptionsSearch,
  }: // eslint-disable-next-line
  any) => {
    const [filtersSetup, setFiltersSetup] = useState<typeof propsFiltersSetup>(null)

    const filterList = useMemo(() => {
      if (!filtersSetup) {
        return []
      }

      return (
        Object.keys(removeObjProperty('general', filtersSetup)).sort((a, b) =>
          PROFILE_FILTERS_ORDER[a] > PROFILE_FILTERS_ORDER[b] ? 1 : -1,
        ) || []
      )
    }, [filtersSetup])

    useEffect(() => {
      if (propsFiltersSetup) {
        setFiltersSetup(propsFiltersSetup)
      }
    }, [propsFiltersSetup])

    if (!filterList.length) {
      return (
        <>
          <FilterSkeletonBox>
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
          </FilterSkeletonBox>
          <FilterSkeletonBox>
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
          </FilterSkeletonBox>
          <FilterSkeletonBox>
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
          </FilterSkeletonBox>
          <FilterSkeletonBox>
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
          </FilterSkeletonBox>
          <FilterSkeletonBox>
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
          </FilterSkeletonBox>
          <FilterSkeletonBox>
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
            <FilterSkeleton animation="wave" />
          </FilterSkeletonBox>
        </>
      )
    }

    return filterList.map(name => (
      <FilterSection
        key={name}
        active={!!selectedFiltersMap?.[name]?.length}
        totalCount={filtersSetup[name].total}
        facets={filtersSetup[name].facets}
        name={name}
        label={FILTER_LABELS_MAP[name] || name}
        handleFilterSelect={onFilterSelect}
        handleClearSection={onClearSection}
        handleExpand={onExpand}
        selectedValues={selectedFiltersMap?.[name]}
        expandedFilterSection={expandedFilter[name]}
        filterSectionExpandAllOptions={filterSectionExpandAllOptions}
        filterSectionOptionsSearch={filterSectionOptionsSearch}
        setFilterSectionExpandAllOptions={setFilterSectionExpandAllOptions}
        setFilterSectionOptionsSearch={setFilterSectionOptionsSearch}
      />
    ))
  },
)

const SearchFilterSection = ({
  filtersSetup,
  searchableEntitiesMap,
  toggleItemFilter,
  selectedFiltersByProfiles,
  isAllProfilesEnabled,
  handleClearSection,
  expandedFilter,
  handleExpandFilter,
  filterSectionExpandAllOptions,
  filterSectionOptionsSearch,
  setFilterSectionExpandAllOptions,
  setFilterSectionOptionsSearch,
  searchHistoryTypeId,
  setSearchHistoryTypeId,
}: // eslint-disable-next-line
any) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }} data-testid="search_filter_section">
      <Filters
        searchableEntitiesMap={searchableEntitiesMap}
        filtersSetup={filtersSetup}
        onFilterSelect={toggleItemFilter}
        selectedFiltersMap={selectedFiltersByProfiles}
        onClearSection={handleClearSection}
        isAllProfilesEnabled={isAllProfilesEnabled}
        expandedFilter={expandedFilter}
        onExpand={handleExpandFilter}
        filterSectionExpandAllOptions={filterSectionExpandAllOptions}
        filterSectionOptionsSearch={filterSectionOptionsSearch}
        setFilterSectionExpandAllOptions={setFilterSectionExpandAllOptions}
        setFilterSectionOptionsSearch={setFilterSectionOptionsSearch}
        searchHistoryTypeId={searchHistoryTypeId}
        setSearchHistoryTypeId={setSearchHistoryTypeId}
      />
    </Box>
  )
}

export default memo(SearchFilterSection)
